import React from 'react';
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from '../CclDataTable';
import Toaster from "../Toaster";
import ApiDefaultAirport from "../../code/api/ApiDefaultAirport";
import DefaultAirport from "../../code/models/DefaultAirport";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";

import "../../css/admindefaulteditors.css";

export default function AdminDefaultAirport() {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [defaultAirportList, setDefaultAirportList] = React.useState<DefaultAirport[] | null>(null);
    const [selectedDefaultAirport, setSelectedDefaultAirport] = React.useState<DefaultAirport | null>(null);
    const [editDefaultAirport, setEditDefaultAirport] = React.useState<DefaultAirport | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [basesOnly, setBasesOnly] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadData();
    }, []);

    React.useEffect(() => {
        loadData();
    }, [basesOnly]);

    async function loadData() {
        ApiDefaultAirport.getDefaultAirports().then(data => {
            if (basesOnly) {
                var bases = data.filter(b => b.isBase);
                setDefaultAirportList(bases);
            } else {
                setDefaultAirportList(data);
            }
        });
    }

    function reloadData() {
        setEditDefaultAirport(null);
        setSelectedDefaultAirport(null);
        setDefaultAirportList(null);
        loadData();
    }

    function saveChanges() {
        setToastMessage("");
        if (anyChanges && !!editDefaultAirport && !!selectedDefaultAirport) {
            let isNew = !selectedDefaultAirport.airportCode;
            ApiDefaultAirport.updateDefaultAirport(editDefaultAirport).then(updatedItem => {
                if (!!updatedItem) {
                    selectedDefaultAirport.airportCode = updatedItem.airportCode;
                    selectedDefaultAirport.airportDescription = updatedItem.airportDescription;
                    selectedDefaultAirport.isBase = updatedItem.isBase;

                    setToastMessage("Default Airport Saved.");

                    if (isNew) {
                        reloadData();
                    } else {
                        setEditDefaultAirport(null);
                        setSelectedDefaultAirport(null);
                        setAnyChanges(false);
                    }
                }
            });
        }
    }

    function cancelChanges() {
        setEditDefaultAirport(null);
        setSelectedDefaultAirport(null);
        setAnyChanges(false);
    }

    function addItem(): void {
        var newRow = new DefaultAirport();
        setSelectedDefaultAirport(newRow);
        setEditDefaultAirport(structuredClone(newRow));
        setAnyChanges(false);
    }

    function deleteItem(confirm: boolean) {
        if (!selectedDefaultAirport || !confirm) return;
        setToastMessage("");

        ApiDefaultAirport.deleteDefaultAirport(selectedDefaultAirport.airportCode).then(success => {
            if (success) {
                setToastMessage(`Default Airport Deleted. (${selectedDefaultAirport.airportCode})`);
                reloadData();
            } else {
                setToastMessage("Unable to delete default Airport.");
            }
        });
    }

    function checkHasChanges() {
        var isAnyChanges = editDefaultAirport !== null &&
            (selectedDefaultAirport?.airportCode !== editDefaultAirport?.airportCode ||
                selectedDefaultAirport?.airportDescription !== editDefaultAirport?.airportDescription ||
                selectedDefaultAirport?.isBase !== editDefaultAirport?.isBase);
        setAnyChanges(isAnyChanges);
    }

    function handleRowSelect(rowData: any) {
        setSelectedDefaultAirport(rowData);
        setEditDefaultAirport(structuredClone(rowData));
        setAnyChanges(false);
    }

    const profileColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "airportCode",
            title: "Airport",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "airportDescription",
            title: "Description",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "isBase",
            title: "Base",
            visible: true,
            type: CclDataTableColumnType.Checkbox
        }
    ];

    function buildEditor() {
        return (<React.Fragment>
            {!!editDefaultAirport && !!selectedDefaultAirport &&
                <div key={"bp" + selectedDefaultAirport.airportCode}
                    className="row adm-default-editor">
                    <div className="col-1">
                        <label htmlFor="editAirport" className="form-label text-nowrap">Airport</label>
                        <input className="form-control" id="editAirport" defaultValue={selectedDefaultAirport.airportCode}
                            maxLength={3} autoComplete={"off"} disabled={!!selectedDefaultAirport.airportCode}
                            onChange={(e) => {
                                editDefaultAirport.airportCode = e.target.value;
                                checkHasChanges();
                            }}></input>
                    </div>
                    <div className="col-3">
                        <label htmlFor="editDescription" className="form-label text-nowrap">Description</label>
                        <input className="form-control" id="editDescription" defaultValue={selectedDefaultAirport.airportDescription}
                            maxLength={200} autoComplete={"off"}
                            onChange={(e) => {
                                editDefaultAirport.airportDescription = e.target.value;
                                checkHasChanges();
                            }}></input>
                    </div>
                    <div className="col-1">
                        <div className="form-check form-switch adm-default-check">
                            <input className="form-check-input" type="checkbox" role="switch" id="editIsBase"
                                defaultChecked={selectedDefaultAirport.isBase}
                                onChange={(e) => {
                                    editDefaultAirport.isBase = e.target.checked;
                                    checkHasChanges();
                                }} />
                            <label className="form-check-label" htmlFor="editIsBase">Is Base</label>
                        </div>
                    </div>
                    <div className="col adm-default-actionbuttons text-nowrap">
                        <button disabled={!anyChanges} className="btn btn-primary"
                            onClick={saveChanges}>Save Changes</button>
                        <button className="btn btn-outline-primary"
                            onClick={cancelChanges}>Cancel</button>
                        <ButtonConfirm enabled={!!selectedDefaultAirport.airportCode} buttonText={"Delete"}
                            confirmTitle={"Default Airport"}
                            confirmMessage={`Delete default airport '${selectedDefaultAirport.airportCode}'?`}
                            buttonSize={ButtonConfirmSize.Normal}
                            cssClass={"btn btn-outline-danger"} onConfirm={deleteItem} />
                    </div>
                </div>}
        </React.Fragment>
        )
    }

    function rebuildAirports(): void {
        ApiDefaultAirport.rebuildAirports().then((success) => {
            if (success) {
                setToastMessage("Bid Period Airports have been rebuilt.");
            } else {
                setToastMessage("Bid Period Airports rebuild failed.");
            }
        });
    }

    return (
        <React.Fragment>
            <div className="container-fluid adm-default-main">
                <div className='adm-default-buttonbar '>
                    <button disabled={!defaultAirportList} className="btn btn-outline-primary" onClick={reloadData}>Reload</button>
                    <button disabled={!defaultAirportList || (!!selectedDefaultAirport && !selectedDefaultAirport.airportCode)} className="btn btn-outline-primary"
                        onClick={addItem}>Add</button>                        
                    <div className="form-check form-switch">
                        <input className="btn-check" type="checkbox" role="switch" id="editBasesOnly" defaultChecked={false} autoComplete="off"
                            onChange={(e) => {
                                setBasesOnly(e.target.checked);
                                checkHasChanges();
                            }} />
                        <label className="btn btn-outline-primary" htmlFor="editBasesOnly">{basesOnly ? "All Airports" : "Bases Only"}</label>
                    </div>
                    <button className="btn btn-outline-primary ms-4" onClick={rebuildAirports}>Rebuild Bid Period Airports</button>
                </div>
                {buildEditor()}
                <div className='row adm-default-table-wrapper'>
                    <CclDataTable
                        name={"profilesTable"}
                        tableData={defaultAirportList || []}
                        columnDefinitions={profileColumnDefs}
                        rowSelect={true}
                        onRowSelect={handleRowSelect}
                        onButtonClick={null} />
                </div>
            </div>
            <Toaster title="Default Airport" subtitle="" message={toastMessage} key={"defapttoast"} />
        </React.Fragment>
    )
}
